import { useState, useRef, useCallback, useEffect } from 'react'
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'
import type { CascadeNode, CascadeEdge } from '@features/dashboards/components/CascadeBuilder/types'
import { NODE_COLORS, DECISION_COLORS, CAPTURE_METHOD_TYPE_CONFIG, VALIDATION_RULESET_CONFIG } from '@features/dashboards/components/CascadeBuilder/types'

interface CascadePreviewProps {
  nodes: CascadeNode[]
  edges: CascadeEdge[]
  country: string
  onNodeClick?: (node: CascadeNode) => void
}

// --- Read-only node rendering ---
function PreviewNode({ node, isSelected, onClick }: { node: CascadeNode; isSelected: boolean; onClick?: () => void }) {
  const colors = NODE_COLORS[node.type] || NODE_COLORS['validation']
  const isCustomer = node.type === 'customer-input'
  const isDecision = node.type === 'decision'
  const isForm = node.type === 'capture-method' && node.captureMethodType === 'form'
  const isValidation = node.type === 'validation'

  const nodeImage = isCustomer ? '/woman.png' : isForm ? '/form.png' : isValidation ? '/policy.png' : null
  const decisionCondition = node.sublabel as keyof typeof DECISION_COLORS | undefined
  const decisionColor = isDecision && decisionCondition && (DECISION_COLORS[decisionCondition] || null)

  return (
    <div
      className={`absolute select-none ${onClick ? 'cursor-pointer' : ''}`}
      style={{ left: node.x, top: node.y }}
      onClick={onClick}
    >
      {/* Hero icon */}
      {nodeImage ? (
        <div className="absolute left-1/2 -translate-x-1/2 -top-7 z-20 pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-white dark:bg-slate-700 shadow-lg border-2 border-white dark:border-slate-600 flex items-center justify-center overflow-hidden">
            <img src={nodeImage} alt={node.label} className="w-10 h-10 object-contain" draggable={false} />
          </div>
        </div>
      ) : (
        <div className="absolute left-1/2 -translate-x-1/2 -top-6 z-20 pointer-events-none">
          <div className={`w-12 h-12 rounded-full shadow-lg border-2 border-white dark:border-slate-600 flex items-center justify-center ${
            decisionColor ? decisionColor.bg : 'bg-white dark:bg-slate-700'
          }`}>
            <span className="text-2xl leading-none">
              {node.captureMethodType ? (CAPTURE_METHOD_TYPE_CONFIG[node.captureMethodType]?.icon || colors.icon) : colors.icon}
            </span>
          </div>
        </div>
      )}

      <div
        data-preview-node={node.id}
        className={`
          relative rounded-xl border-2 shadow-lg mt-7
          ${decisionColor ? `${decisionColor.bg} border-current` : `${colors.bg} ${colors.border}`}
          ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}
          min-w-[140px] max-w-[200px]
        `}
      >
        <div className="px-3 py-2 pt-3 text-center">
          {node.type === 'validation' && node.validationVendor ? (
            <>
              <div className={`text-xs font-bold leading-tight ${colors.text}`}>{node.validationVendor}</div>
              <div className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-tight">Identity Check</div>
            </>
          ) : (
            <>
              <div className={`text-xs font-bold leading-tight ${decisionColor ? decisionColor.text : colors.text}`}>{node.label}</div>
              {node.sublabel && !isDecision && (
                <div className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-tight">{node.sublabel}</div>
              )}
              {node.vendor && (
                <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{node.vendor}</div>
              )}
            </>
          )}
        </div>

        {/* Capture owner badge */}
        {node.type === 'capture-method' && (
          <div className="px-3 pb-1">
            <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${
              node.captureOwner === 'vendor'
                ? 'bg-teal-500/15 text-teal-600 dark:text-teal-400'
                : 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400'
            }`}>
              {node.captureOwner === 'vendor' ? (node.captureVendorName || 'Vendor') : 'Client'}
            </span>
          </div>
        )}

        {/* Collected field pills */}
        {node.collectedFields && node.collectedFields.some(f => f.enabled) && (
          <div className="px-3 pb-2 flex flex-wrap gap-1">
            {node.collectedFields.filter(f => f.enabled).map(f => (
              <span key={f.id} className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${
                f.optional ? 'bg-white/50 dark:bg-white/10 text-[var(--text-muted)]' : 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
              }`}>
                {f.label}{!f.optional && <span className="text-red-500 ml-0.5">*</span>}
              </span>
            ))}
          </div>
        )}

        {/* Validation details */}
        {node.type === 'validation' && (
          <div className="px-3 pb-1 flex flex-wrap gap-1">
            {node.validationRuleset && (
              <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-semibold">
                {(VALIDATION_RULESET_CONFIG as Record<string, { label: string }>)[node.validationRuleset]?.label || node.validationRuleset}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Simple edge rendering using fixed node widths ---
function PreviewEdges({ nodes, edges }: { nodes: CascadeNode[]; edges: CascadeEdge[] }) {
  const NODE_W = 160
  const NODE_H = 65
  const HERO_OFFSET = 28
  const ARROW = 8

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5, overflow: 'visible' }}>
      {edges.map(edge => {
        const fromNode = nodes.find(n => n.id === edge.from)
        const toNode = nodes.find(n => n.id === edge.to)
        if (!fromNode || !toNode) return null

        const isVertical = fromNode.type === 'validation' && toNode.type === 'validation' && toNode.isSecondaryVendor

        if (isVertical) {
          const fx = fromNode.x + NODE_W / 2
          const fy = fromNode.y + HERO_OFFSET + NODE_H
          const tx = toNode.x + NODE_W / 2
          const ty = toNode.y + HERO_OFFSET
          return (
            <g key={edge.id}>
              <path d={`M ${fx} ${fy} L ${tx} ${ty - ARROW}`} fill="none" className="stroke-[var(--text-muted)]" strokeWidth={2} />
              <polygon points={`${tx},${ty} ${tx-4},${ty-ARROW} ${tx+4},${ty-ARROW}`} className="fill-[var(--text-muted)]" />
            </g>
          )
        }

        const fx = fromNode.x + NODE_W
        const fy = fromNode.y + HERO_OFFSET + NODE_H / 2
        const tx = toNode.x
        const ty = toNode.y + HERO_OFFSET + NODE_H / 2

        const dx = tx - fx
        const midX = fx + dx * 0.5

        let strokeClass = 'stroke-[var(--text-muted)]'
        let fillClass = 'fill-[var(--text-muted)]'
        let dashed = false
        if (edge.condition === 'pass') { strokeClass = 'stroke-emerald-500'; fillClass = 'fill-emerald-500' }
        else if (edge.condition === 'fail' || edge.condition === 'no-match') { strokeClass = 'stroke-red-500'; fillClass = 'fill-red-500' }
        else if (edge.condition === 'partial-match') { strokeClass = 'stroke-orange-500'; fillClass = 'fill-orange-500'; dashed = true }
        else if (edge.condition === 'review') { strokeClass = 'stroke-amber-500'; fillClass = 'fill-amber-500' }

        const endX = tx - ARROW
        const pathD = `M ${fx} ${fy} C ${midX} ${fy}, ${midX} ${ty}, ${endX} ${ty}`

        return (
          <g key={edge.id}>
            <path d={pathD} fill="none" className={strokeClass} strokeWidth={2} strokeDasharray={dashed ? '6,4' : undefined} />
            <polygon points={`${tx},${ty} ${endX},${ty-4} ${endX},${ty+4}`} className={fillClass} />
            {edge.label && (
              <>
                <rect x={(fx + tx) / 2 - 30} y={(fy + ty) / 2 - 20} width={60} height={16} rx={4} className="fill-[var(--bg-secondary)]" stroke="var(--border-color)" strokeWidth={1} />
                <text x={(fx + tx) / 2} y={(fy + ty) / 2 - 9} textAnchor="middle" className={`text-[9px] font-semibold ${fillClass.replace('fill-', 'fill-')}`} style={{ fontSize: 9 }}>
                  {edge.label}
                </text>
              </>
            )}
          </g>
        )
      })}
    </svg>
  )
}

// --- Node detail popover ---
function NodeDetail({ node, onClose }: { node: CascadeNode; onClose: () => void }) {
  const colors = NODE_COLORS[node.type] || NODE_COLORS['validation']
  return (
    <div className="absolute top-4 right-4 z-50 w-72 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl overflow-hidden">
      <div className={`px-3 py-2 ${colors.bg} border-b border-[var(--border-color)] flex items-center justify-between`}>
        <span className={`text-xs font-bold ${colors.text}`}>{node.label}</span>
        <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">&times;</button>
      </div>
      <div className="p-3 space-y-2 text-xs text-[var(--text-secondary)] max-h-64 overflow-y-auto">
        {node.vendor && <div><span className="font-medium">Vendor:</span> {node.vendor}</div>}
        {node.validationVendor && <div><span className="font-medium">Vendor:</span> {node.validationVendor}</div>}
        {node.validationRuleset && <div><span className="font-medium">Ruleset:</span> {(VALIDATION_RULESET_CONFIG as Record<string, { label: string }>)[node.validationRuleset]?.label || node.validationRuleset}</div>}
        {node.captureMethodType && <div><span className="font-medium">Method:</span> {(CAPTURE_METHOD_TYPE_CONFIG as Record<string, { label: string }>)[node.captureMethodType]?.label || node.captureMethodType}</div>}
        {node.captureOwner && <div><span className="font-medium">Owner:</span> {node.captureOwner === 'vendor' ? (node.captureVendorName || 'Vendor') : 'Client'}</div>}
        {node.collectedFields && node.collectedFields.some(f => f.enabled) && (
          <div>
            <span className="font-medium">Collected fields:</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {node.collectedFields.filter(f => f.enabled).map(f => (
                <span key={f.id} className="px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 text-[10px]">
                  {f.label}{!f.optional && '*'}
                </span>
              ))}
            </div>
          </div>
        )}
        {node.verifiedFields && node.verifiedFields.some(f => f.enabled) && (
          <div>
            <span className="font-medium">Verified fields:</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {node.verifiedFields.filter(f => f.enabled).map(f => (
                <span key={f.id} className="px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-400 text-[10px]">{f.label}</span>
              ))}
            </div>
          </div>
        )}
        {node.description && <div><span className="font-medium">Description:</span> {node.description}</div>}
        {node.nodeNotes && <div><span className="font-medium">Notes:</span> {node.nodeNotes}</div>}
        {node.price && <div><span className="font-medium">Price:</span> {node.price}</div>}
      </div>
    </div>
  )
}

// --- Main preview component ---
export function CascadePreview({ nodes, edges, country, onNodeClick }: CascadePreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(0.7)
  const [panX, setPanX] = useState(40)
  const [panY, setPanY] = useState(40)
  const [selectedNode, setSelectedNode] = useState<CascadeNode | null>(null)
  const panStart = useRef({ x: 0, y: 0, px: 0, py: 0 })
  const [isPanning, setIsPanning] = useState(false)

  // Fit to view on mount
  useEffect(() => {
    if (nodes.length === 0) return
    const minX = Math.min(...nodes.map(n => n.x))
    const maxX = Math.max(...nodes.map(n => n.x)) + 200
    const minY = Math.min(...nodes.map(n => n.y))
    const maxY = Math.max(...nodes.map(n => n.y)) + 120
    const w = maxX - minX
    const h = maxY - minY
    const containerW = containerRef.current?.clientWidth || 600
    const containerH = containerRef.current?.clientHeight || 300
    const z = Math.min(containerW / w, containerH / h, 1) * 0.85
    setZoom(z)
    setPanX(-minX * z + 20)
    setPanY(-minY * z + 20)
  }, [nodes])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      setZoom(z => Math.max(0.2, Math.min(2, z + (e.deltaY > 0 ? -0.08 : 0.08))))
    }
    // Only pan on horizontal scroll; let vertical scroll pass through to the page
  }, [])

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-preview-node]')) return
    if (target.closest('button')) return
    setSelectedNode(null)
    setIsPanning(true)
    panStart.current = { x: e.clientX, y: e.clientY, px: panX, py: panY }

    const move = (ev: MouseEvent) => {
      setPanX(panStart.current.px + ev.clientX - panStart.current.x)
      setPanY(panStart.current.py + ev.clientY - panStart.current.y)
    }
    const up = () => {
      setIsPanning(false)
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  const fitToView = () => {
    if (nodes.length === 0) return
    const minX = Math.min(...nodes.map(n => n.x))
    const maxX = Math.max(...nodes.map(n => n.x)) + 200
    const minY = Math.min(...nodes.map(n => n.y))
    const maxY = Math.max(...nodes.map(n => n.y)) + 120
    const w = maxX - minX
    const h = maxY - minY
    const containerW = containerRef.current?.clientWidth || 600
    const containerH = containerRef.current?.clientHeight || 300
    const z = Math.min(containerW / w, containerH / h, 1) * 0.85
    setZoom(z)
    setPanX(-minX * z + 20)
    setPanY(-minY * z + 20)
  }

  if (nodes.length === 0) return null

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-[350px] rounded-lg border border-[var(--border-color)] overflow-hidden bg-[var(--bg-primary)] ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      style={{
        backgroundImage: 'radial-gradient(circle, var(--border-color) 1px, transparent 1px)',
        backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
        backgroundPosition: `${panX}px ${panY}px`,
      }}
    >
      {/* Country label */}
      <div className="absolute top-2.5 left-3 z-40 px-2.5 py-1 rounded-md bg-[var(--bg-secondary)]/90 border border-[var(--border-color)] backdrop-blur-sm">
        <span className="text-[10px] font-semibold text-[var(--text-primary)]">{country}</span>
      </div>

      {/* Transformed content */}
      <div
        style={{ transform: `translate(${panX}px, ${panY}px) scale(${zoom})`, transformOrigin: '0 0' }}
        className="absolute inset-0"
      >
        <PreviewEdges nodes={nodes} edges={edges} />
        {nodes.map(node => (
          <PreviewNode
            key={node.id}
            node={node}
            isSelected={selectedNode?.id === node.id}
            onClick={() => {
              setSelectedNode(node)
              onNodeClick?.(node)
            }}
          />
        ))}
      </div>

      {/* Node detail popover */}
      {selectedNode && <NodeDetail node={selectedNode} onClose={() => setSelectedNode(null)} />}

      {/* Zoom controls */}
      <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] shadow-lg p-1 z-40">
        <button onClick={() => setZoom(z => Math.max(0.2, z - 0.1))} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
          <ZoomOut size={12} />
        </button>
        <span className="text-[10px] text-[var(--text-muted)] w-10 text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
          <ZoomIn size={12} />
        </button>
        <div className="w-px h-4 bg-[var(--border-color)]" />
        <button onClick={fitToView} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" title="Fit to view">
          <Maximize2 size={12} />
        </button>
      </div>
    </div>
  )
}
